import React, {useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {CampaignRepository} from "../../../repositories/CampaignRepository";
import LoadingScreen from "../../../components/loading-screen/LoadingScreen";
import useConfirm from "../../../hooks/useConfirm";
import {ACTIVE_POLL_INTERVAL} from "./constants";
import {useCampaignPhoneNumbers} from "./hooks/useCampaignPhoneNumbers";
import {usePolling} from "./hooks/usePolling";
import {useAmiExtensions} from "./hooks/useAmiExtensions";
import {CampaignDetailsHeader} from "./components/campaign-details/CampaignDetailsHeader";
import {CampaignSummary} from "./components/campaign-details/CampaignSummary";
import {CampaignPhoneNumbersTable} from "./components/campaign-details/CampaignPhoneNumbersTable";
import {CallAttemptsDialog} from "./components/campaign-details/CallAttemptsDialog";
import {CampaignStatusChips} from "./components/CampaignStatusChips";
import {CampaignFormDialog} from "./components/campaign-form/CampaignFormDialog";

const callErrorMessage = (error) => {
  if (error?.response?.status === 409) return "This number can't be called now: a call is in progress, it already answered, or it has no attempts left";
  if (error?.response?.status === 400) return "The campaign needs a from number and an extension before calling";
  return error?.response?.data?.message || "Failed to place the call";
};

export const CampaignDetailsPage = ({project}) => {
  const {campaignId} = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [attemptsFor, setAttemptsFor] = useState(null);
  const [callingId, setCallingId] = useState(null);
  const numbers = useCampaignPhoneNumbers(campaignId);
  const {getExtension} = useAmiExtensions(project?.id);
  const confirmDelete = useConfirm("Remove phone number?", "The number and its call attempts will be deleted.", "Remove");
  const confirmCall = useConfirm("Call this number now?", "This places a call right away, outside of the campaign's schedule.", "Call");

  const goBack = () => navigate(`/project/${project?.id}/campaigns`);

  const fetchCampaign = () => {
    CampaignRepository.get(campaignId)
      .then(response => setCampaign(response?.data))
      .catch(() => {
        toast.error("Campaign not found");
        goBack();
      });
  };

  useEffect(() => {
    if (campaignId) fetchCampaign();
  }, [campaignId]);

  const refresh = () => {
    fetchCampaign();
    numbers.refresh();
  };

  // Keep refreshing while any call on this page is ringing
  usePolling(refresh, numbers.rows.some(row => row.status === "ACTIVE"), ACTIVE_POLL_INTERVAL);

  const handleCall = async (row) => {
    const confirmed = await confirmCall({title: `Call ${row.phoneNumber} now?`});
    if (!confirmed) return;
    setCallingId(row.id);
    CampaignRepository.callPhoneNumber(campaignId, row.id)
      .then(response => {
        if (response?.data?.status === "FAILED") {
          toast.error(response?.data?.errorMessage || "The call could not be placed");
        } else {
          toast.success(`Calling ${row.phoneNumber}...`);
        }
        refresh();
      })
      .catch(error => toast.error(callErrorMessage(error)))
      .finally(() => setCallingId(null));
  };

  const handleDelete = async (row) => {
    const confirmed = await confirmDelete({title: `Remove ${row.phoneNumber} from the campaign?`});
    if (!confirmed) return;
    CampaignRepository.deletePhoneNumber(campaignId, row.id)
      .then(() => {
        toast.success("Phone number removed");
        refresh();
      })
      .catch(() => toast.error("Failed to remove phone number"));
  };

  if (campaign === null) {
    return <LoadingScreen/>
  }

  return (
    <Box maxWidth={"xl"} sx={{height: "100%", width: "90%", mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: 2}}>
      <CampaignFormDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        flowUid={campaign.flowUid || project?.id}
        campaign={campaign}
        onSaved={refresh}
      />
      <CallAttemptsDialog
        campaignId={campaignId}
        phoneNumber={attemptsFor}
        projectId={project?.id}
        onClose={() => setAttemptsFor(null)}
      />

      <CampaignDetailsHeader campaign={campaign} onBack={goBack} onRefresh={refresh} onEdit={() => setOpenEdit(true)}/>
      <CampaignSummary campaign={campaign} extension={getExtension(campaign.extensionId)}/>

      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography fontSize={13} color="text.secondary">Filter by status:</Typography>
        <CampaignStatusChips
          neutral
          statusCounts={campaign.statusCounts}
          selected={numbers.status}
          onSelect={numbers.setStatus}
        />
      </Stack>

      <CampaignPhoneNumbersTable
        rows={numbers.rows}
        total={numbers.total}
        loading={numbers.loading}
        page={numbers.page}
        rowsPerPage={numbers.rowsPerPage}
        onPageChange={numbers.setPage}
        onRowsPerPageChange={numbers.setRowsPerPage}
        maxAttempts={campaign.maxAttempts}
        projectId={project?.id}
        callingId={callingId}
        onCall={handleCall}
        onShowAttempts={setAttemptsFor}
        onDelete={handleDelete}
      />
    </Box>
  )
}
